import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import {
  CloseButton,
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Spinner,
  Alert,
  Checkbox,
  Avatar,
  RadioGroup,
  Separator,
  Dialog,
  Portal,
  type RadioGroupValueChangeDetails,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom'
import { toaster } from '@/components/ui/toaster'
import { Tooltip } from '@/components/ui/tooltip';
import { FiEdit3, FiMousePointer } from 'react-icons/fi'
import { Marker } from 'react-map-gl/maplibre'
import type { Map as MapLibreMap } from 'maplibre-gl'
import centroid from '@turf/centroid'
import {
  useRecutPreview, useRecutAccept, useForwardAssignments, useManualRecut, useAvailableCanvassers,
} from '../../../hooks/useCampaign'
import { useTurfReviewData } from '../../../hooks/useTurfReviewData'
import { useJobPolling } from '../../../hooks/useJobPolling'
import { useActiveCanvassing } from '../../../hooks/useActiveCanvassing'
import { useTerraDraw } from '../../../hooks/useTerraDraw'
import type { DrawnTurf } from '../../../hooks/useTerraDraw'
import { TurfMap } from '../../turf/TurfMap'
import { TurfLegendPanel } from '../../turf/TurfLegendPanel'
import { OverlayModeToggle } from '../../turf/OverlayModeToggle'
import { LightSurface, AuthedAvatarImage } from '../../shared'
import { TurfAssignmentPopover } from './TurfAssignmentPopover'
import { TurfRecutDialog } from './TurfRecutDialog'
import { ActiveCanvassingAlert } from './ActiveCanvassingAlert'
import { TurfReviewActionBar } from './TurfReviewActionBar'
import type {
  DailyReviewTurfMapDto,
  DailyTurfCoverageDto,
  RecutPreviewResultDto,
  RecutAvailableCanvasserDto,
  AssignmentMode,
} from '../../../types/campaign'
import type { TurfBoundary } from '../../../types/turfMap'
import type { OverlayMode } from '../../../types/workplaceOverlay'
import { getTurfColor, TURF_COLORS } from '../../../lib/mapConfig'
import { overlayModeToAddressMode, addressModeToOverlayMode } from '../../../utils/geoUtils'

interface Props {
  isOpen: boolean
  onClose: () => void
  campaignId: string
  turfMaps: DailyReviewTurfMapDto[]
  turfCoverage: DailyTurfCoverageDto[]
  reviewDate: string
  assignmentMode?: AssignmentMode
  /** [Canvass this] Applied each time the modal opens (the organizer is about to walk today). */
  initialTargetMode?: 'today' | 'tomorrow'
  /** [Canvass this] Shows the "on the canvasser list" strip and the Skip button. */
  canvassThis?: boolean
  /** Called after each successful save (after its toast). */
  onSaved?: () => void
}

type ModalMode = 'view' | 'auto-preview' | 'loading' | 'recut-dialog'

type TargetMode = 'tomorrow' | 'today'

function nextDay(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  date.setDate(date.getDate() + 1)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function formatDateLabel(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

export function TurfReviewMapModal({
  isOpen, onClose, campaignId, turfMaps, turfCoverage, reviewDate,
  assignmentMode = 'Itinerary', initialTargetMode, canvassThis = false, onSaved,
}: Props) {
  const navigate = useNavigate()
  const isStaticMode = assignmentMode === 'Static'
  const previewMutation = useRecutPreview()
  const acceptMutation = useRecutAccept(campaignId)
  const forwardMutation = useForwardAssignments(campaignId)
  const manualRecutMutation = useManualRecut(campaignId)

  // Snapshot turfmaps on open so cache invalidations don't shift the stepper
  const snapshotRef = useRef<DailyReviewTurfMapDto[]>([])
  useEffect(() => {
    if (isOpen) {
      snapshotRef.current = turfMaps.filter(tm => tm.isActive)
    }
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps
  const activeTurfMaps = snapshotRef.current.length > 0
    ? snapshotRef.current
    : turfMaps.filter(tm => tm.isActive)

  const [mode, setMode] = useState<ModalMode>('view')
  const [preview, setPreview] = useState<RecutPreviewResultDto | null>(null)
  // turfId-based assignments (view mode) or turfIndex-based (auto-preview mode)
  const [assignments, setAssignments] = useState<Map<string, string | null>>(new Map())
  const [forwardToItinerary, setForwardToItinerary] = useState(true)
  const [overlayMode, setOverlayMode] = useState<OverlayMode>('home')
  const [targetMode, setTargetMode] = useState<TargetMode>('tomorrow')
  useEffect(() => {
    if (isOpen && initialTargetMode) setTargetMode(initialTargetMode)
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps
  const [hideCompleted, setHideCompleted] = useState(true)
  const [fitToBounds, setFitToBounds] = useState(true)

  // Auto-recut dialog state
  const [recutTurfCount, setRecutTurfCount] = useState(1)

  // Recut-preview job polling state (WI-41)
  const [recutJobId, setRecutJobId] = useState<string | null>(null)
  const { data: recutJob, isTerminal: recutTerminal } = useJobPolling(recutJobId)

  // Assignment popover state
  const [popoverTurfKey, setPopoverTurfKey] = useState<string | null>(null) // turfId or turfIndex string
  const popoverAnchorRef = useRef<HTMLDivElement>(null)

  // Terra-draw refs
  const mapRef = useRef<MapLibreMap | null>(null)

  const currentTurfMap = activeTurfMaps[0] ?? null

  // Sync overlay mode to current TurfMap's saved addressMode when stepping
  useEffect(() => {
    if (currentTurfMap) {
      setOverlayMode(addressModeToOverlayMode(currentTurfMap.addressMode))
    }
  }, [currentTurfMap?.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const targetDate = targetMode === 'tomorrow' ? nextDay(reviewDate) : reviewDate
  // [Canvass this, CT-16/17] reviewDate/targetDate are client-local yyyy-MM-dd, so "today" matches the server's tzOffset read.
  const { data: activeCanvassing } = useActiveCanvassing(currentTurfMap?.id, targetDate, isOpen)

  // Fetch and compute map data via extracted hook
  const currentTurfMapId = currentTurfMap?.id ?? undefined
  const {
    turfMapDetail, households,
    turfBoundaries, householdMarkers,
    workplaceMarkers, hybridHouseholds,
    contactLocations, turfMembership,
  } = useTurfReviewData({ isOpen, turfMapId: currentTurfMapId, overlayMode })

  // Fetch available canvassers for assignment popover
  const { data: availableCanvassersData } = useAvailableCanvassers(
    isOpen ? campaignId : undefined,
    isOpen ? targetDate : undefined
  )
  const availableCanvassers: RecutAvailableCanvasserDto[] = useMemo(() => {
    if (preview?.availableCanvassers) return preview.availableCanvassers
    return (availableCanvassersData?.canvassers ?? []).map(c => ({
      grantId: c.grantId,
      fullName: c.fullName,
      portraitUrl: c.portraitUrl ?? null,
    }))
  }, [preview?.availableCanvassers, availableCanvassersData])

  const canvasserLookup = useMemo(() => {
    const map = new Map<string, { fullName: string; portraitUrl: string | null }>()
    availableCanvassers.forEach(c => {
      map.set(c.grantId, { fullName: c.fullName, portraitUrl: c.portraitUrl ?? null })
    })
    return map
  }, [availableCanvassers])

  // =========================================================================
  // Terra-draw hook for polygon editing
  // =========================================================================

  const handleTerraDrawTurfsChange = useCallback((_turfs: DrawnTurf[]) => {
    // Update occurs via hook state; no extra action needed here
  }, [])

  const {
    drawnTurfs,
    isDirty,
    isMapReady,
    isDrawingMode,
    loadExistingPolygons,
    revertToOriginal,
    getEditedPolygons,
    startDrawing,
    stopDrawing,
    deleteTurf,
    updateTurfLabel,
    flyToTurf,
    handleMapLoad: terraDrawMapLoad,
  } = useTerraDraw({
    mapRef,
    contacts: contactLocations,
    onTurfsChange: handleTerraDrawTurfsChange,
    editOnly: false,
    turfMapLabel: currentTurfMap?.label,
    overlayMode,
  })

  // Track which turfMap was loaded into terra-draw to avoid redundant loads
  const loadedTurfMapIdRef = useRef<string | null>(null)

  const handleMapLoad = useCallback((map: MapLibreMap) => {
    mapRef.current = map
    loadedTurfMapIdRef.current = null // Reset so polygons reload into new terra-draw
    terraDrawMapLoad(map)
  }, [terraDrawMapLoad])
  useEffect(() => {
    // Wait for households too: loading polygons before membership arrives would seed zero
    // counts, and useTerraDraw re-derives client-side when the contact list later grows.
    if (!isMapReady || !turfMapDetail || !turfBoundaries.length || !households) return
    if (loadedTurfMapIdRef.current === turfMapDetail.id) return
    loadedTurfMapIdRef.current = turfMapDetail.id

    // Seed each turf with its server membership so the legend matches the turf overview;
    // terra-draw only re-derives counts client-side once a polygon is edited.
    const polygons = turfBoundaries.map((b, idx) => ({
      id: b.id,
      geometry: b.geoJson.geometry,
      label: b.label,
      color: b.color ?? TURF_COLORS[idx % TURF_COLORS.length],
      index: idx + 1,
      contactIds: turfMembership[b.id]?.contactIds ?? [],
      householdCount: turfMembership[b.id]?.householdCount ?? 0,
    }))
    loadExistingPolygons(polygons)
    // Disable auto-fit after initial load so vertex dragging doesn't re-center the map
    // Use a short delay to let the initial fitBounds animation complete first
    const timer = setTimeout(() => setFitToBounds(false), 600)
    return () => clearTimeout(timer)
  }, [isMapReady, turfMapDetail, turfBoundaries, households, turfMembership, loadExistingPolygons])

  // Initialize assignments from turf detail
  useEffect(() => {
    if (turfMapDetail?.turfs && mode === 'view') {
      const initial = new Map<string, string | null>()
      turfMapDetail.turfs.forEach((t, idx) => {
        initial.set(String(idx + 1), t.assignedTo ?? null)
      })
      setAssignments(initial)
    }
  }, [turfMapDetail, mode])

  // =========================================================================
  // Compute polygon centroids for canvasser portrait bubbles
  // =========================================================================

  const turfCentroids = useMemo(() => {
    // Use drawnTurfs as single source of truth for centroids (works in both view and auto-preview)
    return drawnTurfs.map(dt => {
      try {
        const center = centroid({ type: 'Feature', geometry: dt.polygon.geometry, properties: {} })
        const turfKey = String(dt.index)
        const canvasserId = assignments.get(turfKey) ?? null
        const canvasser = canvasserId ? canvasserLookup.get(canvasserId) : null
        return {
          key: dt.id,
          turfKey,
          lng: center.geometry.coordinates[0],
          lat: center.geometry.coordinates[1],
          canvasserName: canvasser?.fullName ?? null,
          portraitUrl: canvasser?.portraitUrl ?? null,
          canvasserId,
        }
      } catch { return null }
    }).filter(Boolean) as { key: string; turfKey: string; lng: number; lat: number; canvasserName: string | null; portraitUrl: string | null; canvasserId: string | null }[]
  }, [drawnTurfs, assignments, canvasserLookup])

  // =========================================================================
  // Boundaries for the map (use drawnTurfs when editing, else preview or static)
  // =========================================================================

  const displayBoundaries: TurfBoundary[] = useMemo(() => {
    // In auto-preview mode, terra-draw owns rendering (polygons were loaded via loadExistingPolygons)
    if (mode === 'auto-preview') return []
    // In view mode, TurfMap renders the base boundaries. terra-draw renders edit overlays.
    // Don't double-render: return empty when terra-draw is active
    if (isMapReady && drawnTurfs.length > 0) return []
    return turfBoundaries
  }, [mode, turfBoundaries, isMapReady, drawnTurfs])

  // =========================================================================
  // Action handlers
  // =========================================================================

  const handleAutoRecutDialog = () => {
    // [CT-15] Default to the current turf count, not the canvasser headcount.
    const recommended = currentTurfMap?.turfCount || 1
    setRecutTurfCount(recommended)
    setMode('recut-dialog')
  }

  const handleAutoRecut = async () => {
    if (!currentTurfMap) return
    setMode('loading')
    try {
      const { jobId } = await previewMutation.mutateAsync({
        turfMapId: currentTurfMap.id,
        request: { targetDate, targetTurfCount: recutTurfCount, addressMode: overlayModeToAddressMode(overlayMode) },
      })
      setRecutJobId(jobId)
    } catch {
      toaster.create({ title: 'Auto re-cut preview failed', type: 'error', duration: 5000 })
      setMode('view')
    }
  }

  // Drives the recut-preview job to completion (WI-41). Branches on `!recutTerminal` alone — NOT
  // `!recutTerminal || !recutJob` — because a 404 (job the caller doesn't own, or that doesn't
  // exist) reports isTerminal=true with job undefined; guarding on `!job` too would swallow that
  // case and hang the loading overlay forever. Nothing is invalidated here:
  // PreviewRecutFromRemainderAsync never persists anything server-side (all reads are
  // AsNoTracking, no SaveChangesAsync in that method) — it only returns cluster/boundary data for
  // the client to load into terra-draw, so there is no server cache to refresh.
  useEffect(() => {
    if (!recutTerminal) return

    if (recutJob?.status === 'Succeeded') {
      const result = recutJob.result as RecutPreviewResultDto
      setPreview(result)
      // Initialize assignments from suggestions using turfIndex as key
      const newAssignments = new Map<string, string | null>()
      result.turfs.forEach(t => {
        newAssignments.set(String(t.turfIndex), t.suggestedCanvasserId)
      })
      setAssignments(newAssignments)
      // Load preview polygons into terra-draw (editable, replaces originals)
      const previewPolygons = result.turfs.map(t => {
        const geometry = JSON.parse(t.boundaryGeoJson)
        return {
          id: `preview-${t.turfIndex}`,
          geometry,
          label: t.label,
          color: getTurfColor(t.turfIndex),
          index: t.turfIndex,
          contactIds: t.contactIds,
          householdCount: t.householdCount,
        }
      })
      loadExistingPolygons(previewPolygons)
      setMode('auto-preview')
    } else {
      toaster.create({
        title: 'Re-cut preview failed',
        description: recutJob?.errorMessage ?? 'An error occurred.',
        type: 'error',
        duration: 5000,
      })
      setMode('view')
    }
    setRecutJobId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recutTerminal, recutJob])

  const handleAcceptRecut = async () => {
    if (!currentTurfMap || !preview) return
    try {
      // Use drawnTurfs which may have been edited (vertices dragged) since preview was loaded.
      // Send only the (possibly edited) boundary + assignment: AcceptRecutAsync re-derives each
      // turf's contact membership authoritatively from the polygon server-side. Sending
      // dt.contactIds was dead weight and, for a turf with >5000 contacts, tripped the server's
      // MaxBulkContactIds model-validation cap → 400 on Accept & Finish.
      await acceptMutation.mutateAsync({
        turfMapId: currentTurfMap.id,
        request: {
          targetDate,
          addressMode: overlayModeToAddressMode(overlayMode),
          turfAssignments: drawnTurfs.map(dt => ({
            turfIndex: dt.index,
            boundaryGeoJson: JSON.stringify(dt.polygon.geometry),
            assignedCanvasserId: assignments.get(String(dt.index)) ?? null,
          })),
        },
      })
      toaster.create({ title: `Re-cut accepted for ${currentTurfMap.label}`, type: 'success', duration: 3000 })
      advanceOrFinish()
      onSaved?.()
    } catch {
      toaster.create({ title: 'Accept failed', type: 'error', duration: 5000 })
    }
  }

  const handleSaveUpdatedTurfs = async () => {
    if (!currentTurfMap) return
    try {
      const polygons = getEditedPolygons(assignments)
      await manualRecutMutation.mutateAsync({
        turfMapId: currentTurfMap.id,
        request: {
          targetDate,
          addressMode: overlayModeToAddressMode(overlayMode),
          polygons,
          forwardToItinerary,
        },
      })
      toaster.create({ title: `Updated turfs saved for ${currentTurfMap.label}`, type: 'success', duration: 3000 })
      advanceOrFinish()
      onSaved?.()
    } catch {
      toaster.create({ title: 'Save failed', type: 'error', duration: 5000 })
    }
  }

  const handleAcceptAsIs = async () => {
    if (!currentTurfMap) return
    if (!forwardToItinerary) {
      // Just skip — no API call needed
      advanceOrFinish()
      // [Canvass this] Skipping still lands the organizer in canvasser mode, as the save paths do.
      if (canvassThis) onSaved?.()
      return
    }
    try {
      // Build assignment overrides — assignments are keyed by turfIndex
      const overrides = turfMapDetail?.turfs
        ?.map((t, idx) => {
          const currentAssignment = assignments.get(String(idx + 1))
          const originalAssignment = t.assignedTo ?? null
          if (currentAssignment === originalAssignment) return null
          return {
            turfId: t.id,
            assignedCanvasserId: currentAssignment ?? null,
          }
        })
        .filter((o): o is NonNullable<typeof o> => o !== null)

      await forwardMutation.mutateAsync({
        turfMapId: currentTurfMap.id,
        request: {
          fromDate: reviewDate,
          targetDate,
          assignmentOverrides: overrides?.length ? overrides : undefined,
        },
      })
      toaster.create({ title: `Assignments forwarded for ${currentTurfMap.label}`, type: 'success', duration: 3000 })
      advanceOrFinish()
      onSaved?.()
    } catch {
      toaster.create({ title: 'Forward assignments failed', type: 'error', duration: 5000 })
    }
  }

  const handleAssign = (turfKey: string, canvasserId: string | null) => {
    setAssignments(prev => {
      const next = new Map(prev)
      next.set(turfKey, canvasserId)
      return next
    })
  }

  const handleTurfClick = (turfKey: string) => {
    setPopoverTurfKey(turfKey)
  }

  const handleBoundaryClick = (boundaryId: string) => {
    // Map boundary ID (turf GUID) → turfIndex for consistent assignment keying
    const idx = turfBoundaries.findIndex(b => b.id === boundaryId)
    if (idx >= 0) {
      handleTurfClick(String(idx + 1))
    }
  }

  const handleRevert = () => {
    if (mode === 'auto-preview') {
      // Revert from auto-preview back to view mode
      setMode('view')
      setPreview(null)
      // Reload original polygons
      if (turfBoundaries.length > 0) {
        const polygons = turfBoundaries.map((b, idx) => ({
          id: b.id,
          geometry: b.geoJson.geometry,
          label: b.label,
          color: b.color ?? TURF_COLORS[idx % TURF_COLORS.length],
          index: idx + 1,
          contactIds: turfMembership[b.id]?.contactIds ?? [],
          householdCount: turfMembership[b.id]?.householdCount ?? 0,
        }))
        loadExistingPolygons(polygons)
      }
      // Restore original assignments (keyed by turfIndex)
      if (turfMapDetail?.turfs) {
        const initial = new Map<string, string | null>()
        turfMapDetail.turfs.forEach((t, idx) => {
          initial.set(String(idx + 1), t.assignedTo ?? null)
        })
        setAssignments(initial)
      }
    } else {
      // Revert polygon edits in view mode
      revertToOriginal()
    }
  }

  // =========================================================================
  // Navigation
  // =========================================================================

  const advanceOrFinish = () => {
    handleClose()
  }

  const handleClose = () => {
    setTargetMode('tomorrow')
    setMode('view')
    setPreview(null)
    setAssignments(new Map())
    setPopoverTurfKey(null)
    loadedTurfMapIdRef.current = null
    setFitToBounds(true)
    setRecutJobId(null) // stop polling an in-flight recut-preview job if the modal closes early
    onClose()
  }

  // Coverage stats
  const currentCoverage = useMemo(() => {
    if (!currentTurfMap) return []
    return turfCoverage.filter(tc => tc.turfMapId === currentTurfMap.id)
  }, [currentTurfMap, turfCoverage])

  const totalMembers = currentTurfMap?.totalContacts ?? 0
  const totalVisited = currentTurfMap?.visitedContacts ?? 0
  const coveragePct = totalMembers > 0 ? Math.round((totalVisited / totalMembers) * 100) : 0

  // Build coverage map for TurfLegendPanel (turfIndex string → { visited, total })
  const coverageMap = useMemo(() => {
    if (!currentCoverage.length || !turfMapDetail?.turfs) return undefined
    const map = new Map<string, { visited: number; total: number }>()
    turfMapDetail.turfs.forEach((t, idx) => {
      const cov = currentCoverage.find(c => c.turfId === t.id)
      if (cov) {
        map.set(String(idx + 1), { visited: cov.visitedMembers, total: cov.totalMembers })
      }
    })
    return map.size > 0 ? map : undefined
  }, [currentCoverage, turfMapDetail])

  const addInSource = mode === 'auto-preview' && preview ? preview : turfMapDetail
  const addInStopCount = addInSource?.addInStopCount ?? 0
  const addInPeopleCount = addInSource?.addInPeopleCount ?? 0

  const isSaving = manualRecutMutation.isPending || acceptMutation.isPending || forwardMutation.isPending

  // Popover helper — popoverTurfKey is always a turfIndex string now
  const popoverInfo = useMemo(() => {
    if (!popoverTurfKey) return null
    const idx = parseInt(popoverTurfKey, 10)
    if (mode === 'auto-preview' && preview) {
      const turf = preview.turfs.find(t => t.turfIndex === idx)
      return turf ? { label: turf.label, index: idx } : null
    }
    const turf = turfMapDetail?.turfs[idx - 1] // 1-based index → 0-based array
    return turf ? { label: turf.label, index: idx } : null
  }, [popoverTurfKey, mode, preview, turfMapDetail])

  return (
    <Dialog.Root open={isOpen} size={{ base: 'full', md: 'xl' }} scrollBehavior="inside" onOpenChange={e => {
      if (!e.open) {
        handleClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxH="90vh">
            <Dialog.Header pb={2}>
              <VStack align="start" gap={2}>
                <Text>Turf Review {isStaticMode ? '— Static Assignments' : `— ${formatDateLabel(targetDate)}`}</Text>
                {!isStaticMode && (
                  <RadioGroup.Root
                    size="sm"
                    value={targetMode}
                    onValueChange={(details: RadioGroupValueChangeDetails) => setTargetMode(details.value as TargetMode)}>
                    <HStack gap={4}>
                      <RadioGroup.Item value="tomorrow" colorPalette="brand"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>
                          <Text fontSize="sm">Setting for Tomorrow ({formatDateLabel(nextDay(reviewDate))})</Text>
                        </RadioGroup.ItemText></RadioGroup.Item>
                      <RadioGroup.Item value="today" colorPalette="orange"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>
                          <Text fontSize="sm">Setting for Today ({formatDateLabel(reviewDate)})</Text>
                        </RadioGroup.ItemText></RadioGroup.Item>
                    </HStack>
                  </RadioGroup.Root>
                )}
                {canvassThis && (
                  <Alert.Root status="info" borderRadius="md" py={2} fontSize="sm">
                    <Alert.Indicator />
                    <VStack align="start" gap={2}>
                      <Text>You&apos;re on the canvasser list — pick a turf for yourself or recut.</Text>
                      <Button size="sm" variant="outline" minH="44px" onClick={() => navigate('/canvasser')}>
                        Skip — go canvass
                      </Button>
                    </VStack>
                  </Alert.Root>
                )}
                {!isStaticMode && targetMode === 'today' && (
                  <Alert.Root status="warning" borderRadius="md" py={2} fontSize="sm">
                    <Alert.Indicator />
                    Warning: Any changes to Turfs or Turf-Assignments during an active canvassing shift will override the canvasser&apos;s current schedule.
                  </Alert.Root>
                )}
              </VStack>
            </Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>

            <Dialog.Body pb={6}>
              <VStack gap={4} align="stretch">
                {/* TurfMap info card */}
                {currentTurfMap && (
                  <Box p={3} bg="info.subtle" borderRadius="md">
                    <HStack justify="space-between">
                      <Box>
                        <Text fontWeight="medium">{currentTurfMap.label}</Text>
                        <Text fontSize="sm" color="gray.600">
                          {currentTurfMap.turfCount} turfs · {totalMembers} contacts · {totalVisited} visited
                        </Text>
                      </Box>
                      <Badge colorPalette={coveragePct >= 80 ? 'green' : coveragePct >= 40 ? 'yellow' : 'red'}>
                        {coveragePct}% done
                      </Badge>
                    </HStack>
                  </Box>
                )}

                {/* Map controls */}
                <HStack justify="space-between">
                  <Checkbox.Root
                    size="sm"
                    onCheckedChange={(e) => setHideCompleted(e.checked === true)}
                    checked={hideCompleted}
                  ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                    <Text fontSize="sm">Hide completed households</Text>
                  </Checkbox.Label></Checkbox.Root>
                  <OverlayModeToggle mode={overlayMode} onChange={setOverlayMode} />
                </HStack>

                {/* Map area — TurfMap always mounted to preserve terra-draw state.
                    The whole map island (canvas + on-map overlays: drawing toolbar, loading
                    overlay, recut dialog, floating legend, canvasser markers) stays LIGHT in
                    dark mode via LightSurface (§5.6); the modal frame around it goes dark. */}
                <LightSurface>
                <Box position="relative">
                  <TurfMap
                    boundaries={displayBoundaries}
                    households={overlayMode === 'work' ? [] : (overlayMode === 'hybrid' && hybridHouseholds ? hybridHouseholds : householdMarkers).filter(h => !hideCompleted || h.visitedCount < h.contactCount || h.needsRevisitCount > 0)}
                    workplaces={overlayMode !== 'home' ? workplaceMarkers : undefined}
                    onBoundaryClick={handleBoundaryClick}
                    onMapLoad={handleMapLoad}
                    disableInteractions={isDrawingMode}
                    height="400px"
                    fitToBounds={fitToBounds}
                  >
                    {/* Canvasser portrait bubbles at polygon centroids */}
                    {turfCentroids.map(c => c.canvasserName && (
                      <Marker
                        key={c.key}
                        longitude={c.lng}
                        latitude={c.lat}
                        anchor="center"
                        onClick={(e) => {
                          e.originalEvent.stopPropagation()
                          handleTurfClick(c.turfKey)
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <Tooltip contentProps={{ fontSize: "xs" }} content={c.canvasserName} showArrow>
                          <Box>
                            <Avatar.Root size="sm" border="2px solid white" shadow="md"><Avatar.Fallback name={c.canvasserName} /><AuthedAvatarImage src={c.portraitUrl} /></Avatar.Root>
                          </Box>
                        </Tooltip>
                      </Marker>
                    ))}
                  </TurfMap>

                  {/* Drawing toolbar — hidden during loading/dialog */}
                  {mode !== 'loading' && mode !== 'recut-dialog' && (
                    <Box
                      position="absolute"
                      bottom={4}
                      right={4}
                      bg="surface"
                      px={3}
                      py={2}
                      borderRadius="md"
                      shadow="lg"
                      zIndex={10}
                    >
                      <HStack gap={3}>
                        {isDrawingMode ? (
                          <>
                            <Tooltip content="Cancel drawing (or right-click)">
                              <Button size="sm" colorPalette="gray" onClick={stopDrawing}><FiMousePointer />Cancel
                                                          </Button>
                            </Tooltip>
                            <HStack gap={2}>
                              <Box
                                w="8px"
                                h="8px"
                                borderRadius="full"
                                bg="green.400"
                                animation="pulse 2s infinite"
                              />
                              <Text fontSize="xs" color="gray.600">
                                Drawing... right-click to cancel
                              </Text>
                            </HStack>
                          </>
                        ) : (
                          <>
                            <Tooltip content="Start drawing a turf polygon">
                              <Button size="sm" colorPalette="brand" onClick={startDrawing}><FiEdit3 />Draw Turf
                                                          </Button>
                            </Tooltip>
                            <Text fontSize="xs" color="gray.500">
                              Click a polygon to select &amp; edit vertices
                            </Text>
                          </>
                        )}
                      </HStack>
                    </Box>
                  )}

                  {/* Loading overlay */}
                  {mode === 'loading' && (
                    <VStack
                      position="absolute" top={0} left={0} right={0} bottom={0}
                      bg="gray.50" borderRadius="lg" zIndex={20}
                      justify="center"
                    >
                      <Spinner size="lg" color="accent" />
                      <Text color="gray.500" fontSize="sm">Generating optimal turf boundaries...</Text>
                    </VStack>
                  )}

                  {/* Recut dialog overlay */}
                  {mode === 'recut-dialog' && (
                    <TurfRecutDialog
                      availableCanvasserCount={availableCanvassers.length}
                      targetDate={targetDate}
                      recutTurfCount={recutTurfCount}
                      onRecutTurfCountChange={setRecutTurfCount}
                      onCancel={() => setMode('view')}
                      onGenerate={handleAutoRecut}
                      isLoading={previewMutation.isPending}
                    />
                  )}

                  {/* Turf legend panel with assignments */}
                  {mode !== 'loading' && mode !== 'recut-dialog' && drawnTurfs.length > 0 && (
                    <TurfLegendPanel
                      turfs={drawnTurfs}
                      methods={{ deleteTurf, updateTurfLabel, flyToTurf }}
                      assignments={assignments}
                      canvasserLookup={canvasserLookup}
                      onAssignmentClick={handleTurfClick}
                      coverageData={coverageMap}
                      showHouseholdStatusKey={overlayMode !== 'work'}
                    />
                  )}

                  {/* Hidden popover anchor */}
                  <Box ref={popoverAnchorRef} position="absolute" top="50%" left="50%" />
                </Box>
                </LightSurface>

                {/* Assignment popover */}
                {popoverTurfKey != null && popoverInfo && (
                  <TurfAssignmentPopover
                    isOpen={popoverTurfKey != null}
                    onClose={() => setPopoverTurfKey(null)}
                    turfIndex={popoverInfo.index}
                    turfLabel={popoverInfo.label}
                    currentCanvasserId={assignments.get(popoverTurfKey) ?? null}
                    availableCanvassers={availableCanvassers}
                    onAssign={(_, canvasserId) => handleAssign(popoverTurfKey, canvasserId)}
                    anchorRef={popoverAnchorRef as React.RefObject<HTMLDivElement>}
                  />
                )}

                {/* Auto-preview info bar */}
                {mode === 'auto-preview' && preview && (
                  <Alert.Root status="info" borderRadius="md" py={2}>
                    <Alert.Indicator />
                    <Text fontSize="sm">
                      {preview.unvisitedContactCount} unvisited contacts → {preview.turfs.length} turfs
                      ({preview.availableCanvassers.length} canvassers available)
                    </Text>
                  </Alert.Root>
                )}

                {mode !== 'loading' && <ActiveCanvassingAlert data={activeCanvassing} />}

                {/* O4: a re-cut builds new turfs from the universe pool, so add-in stops are dropped.
                    Auto-preview reads the counts off the preview; manual mode (no preview yet) off
                    the turf-map detail. */}
                {addInStopCount > 0 && (
                  <Alert.Root status="warning" borderRadius="md" py={2}>
                    <Alert.Indicator />
                    <Text fontSize="sm">
                      {`${addInStopCount} added stop(s) (${addInPeopleCount} people) won't carry over to the new turfs.`}
                    </Text>
                  </Alert.Root>
                )}

                <Separator />

                {/* Action buttons */}
                <TurfReviewActionBar
                  mode={mode}
                  isDirty={isDirty}
                  isLastStep={true}
                  isSaving={isSaving}
                  isStaticMode={isStaticMode}
                  forwardToItinerary={forwardToItinerary}
                  onForwardToItineraryChange={setForwardToItinerary}
                  targetDateLabel={formatDateLabel(targetDate)}
                  onAutoRecutDialog={handleAutoRecutDialog}
                  onRevert={handleRevert}
                  onSaveUpdatedTurfs={handleSaveUpdatedTurfs}
                  onAcceptAsIs={handleAcceptAsIs}
                  onAcceptRecut={handleAcceptRecut}
                />
              </VStack>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
